import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../app/store";
import { setProgressBarActive } from "reducers/pageReducer";
import {
  DeleteCarProps,
  GetCarByTagProps,
  GetCarProps,
  GetCarRequestsProps,
  GetCarsProps,
  GetLiveCarsProps,
  GetUserCarsProps,
  UpdateCarProps,
} from "declarations/carServices";
import {
  DeleteCar,
  GetCar,
  GetCarByTag,
  GetCars,
  GetLiveCars,
  GetUserCars,
  RepostCar,
  UpdateCar,
  GetCarRequests,
} from "api/services/car";
import { setCar, setCars, setLiveCars } from "reducers/carReducer";
import { validateUpdateCarData } from "validations/dataValidation";
import { setCarUpdateReload } from "reducers/reloadReducer";
import { useNavigate } from "react-router-dom";
import { CarType } from "types/loadedData";
import { Paths } from "router/Paths";
import { enqueueSnackbar } from "notistack";
import { setCarRequests } from "reducers/requestReducer";
import { useAsyncOperation } from "./useAsyncOperation";

export default function useCars() {
  const { execute } = useAsyncOperation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { carUpdateReload } = useSelector((state: RootState) => state.reload);
  const { liveCars, car, cars } = useSelector((state: RootState) => state.car);
  const { carRequests } = useSelector((state: RootState) => state.request);

  const getCars = async (
    query: GetCarsProps,
    { reload, local, more }: { reload?: boolean; local?: boolean; more?: boolean }
  ) => {
    if (
      cars &&
      cars.length > 0 &&
      !reload &&
      !local &&
      !more &&
      !carUpdateReload
    )
      return;
    try {
      const response = await execute(() => GetCars(query), {
        errorMessage: "Fetching Cars Failed",
      });

      const { cars: loadedCars, count, message, status } = response;
      if (status !== 200) throw new Error(message);
    
      let totalCars = [...loadedCars];
    
      if (local) return totalCars;
      if (more) totalCars = [...cars, ...loadedCars];

      dispatch(setCars(totalCars));
      dispatch(setCarUpdateReload(false));
    } catch (error) {}
  };

  const getLiveCars = async (
    { setLoading, limit, offset, search, statusChangedBy }: GetLiveCarsProps,
    { reload, local, more }: { reload?: boolean; local?: boolean; more?: boolean }
  ) => {
    if (
      liveCars &&
      liveCars.length > 0 &&
      !reload &&
      !local &&
      !more &&
      !carUpdateReload
    )
      return;
    try {
      const response = await execute(
        () => GetLiveCars({ limit, offset, search, setLoading, statusChangedBy }),
        { errorMessage: "Fetching Live Cars Failed" }
      );
      const { cars, count, message, status } = response;

      if (status !== 200) throw new Error(message);

      let totalCars = [...cars];

      if (local) return totalCars;
      if (more) totalCars = [...liveCars, ...cars];

      dispatch(setLiveCars(totalCars));
      dispatch(setCarUpdateReload(false));
    } catch (error) {}
  };

  const getCar = async ({ setLoading, id }: GetCarProps) => {
    if (car?._id === id) return car;
    try {
      await execute(() => GetCar({ setLoading, id }), {
        errorMessage: "Fetching Car Failed",

        onSuccess: (response) => {
          const { car: loadedCar, message, status } = response;

          if (status !== 200) throw new Error(message);
          dispatch(setCar(loadedCar));
        },
      });
    } catch (error) {}
  };

  const getCarByTag = async (query: GetCarByTagProps) => {
    if (car?.tag === query.tag) return car;
    try {
      await execute(() => GetCarByTag({ ...query }), {
        errorMessage: "Fetching Car by Tag Failed",

        onSuccess: (response) => {
          const { car: loadedCar, message, status } = response;

          if (status !== 200) throw new Error(message);

          dispatch(setCar(loadedCar));
          navigate(`${Paths.cars}/${loadedCar._id}`);
        },
      });
    } catch (error) {}
  };

  const updateCar = async (props: UpdateCarProps) => {
    const isValid = validateUpdateCarData({
      data: {
        id: props.id,
        make: props.make,
        existingImages: props.existingImages,
        description: props.description,
        fuelType: props.fuelType,
        listingType: props.listingType,
        mileage: props.mileage,
        model: props.model,
        phone: props.phone,
        price: props.price,
        transmissionType: props.transmissionType,
        usageType: props.usageType,
        year: props.year,
        buyerPhone: props.buyerPhone,
      },
    });
    if (!isValid) return;
    try {
      await execute(() => UpdateCar(props), {
        successMessage: "Car Updated Successfully",
        errorMessage: "Updating Car Failed",
        onSuccess: (response) => {
          const { car: loadedCar, message, status, error } = response;

          if (status !== 200) throw new Error(message || error);

          dispatch(setCar(loadedCar));
          dispatch(setCarUpdateReload(true));
          if (!props.stayOnPage) {
            setTimeout(() => {
              navigate(-1);
            }, 500);
          }
        },
      });
    } catch (error) {}
  };

  const getUserCars = async ({
    setLoading,
    limit,
    offset,
    id,
  }: GetUserCarsProps): Promise<{ cars: CarType[]; count: number }> => {
    try {
      const response = await execute(
        () => GetUserCars({ setLoading, id, limit, offset }),
        { errorMessage: "Fetching User Cars Failed" }
      );
      const { cars: loadedCars, message, status, count } = response;

      if (status !== 200) throw new Error(message);

      return { cars: loadedCars, count };
    } catch (error) {
      return { cars: [], count: 0 };
    }
  };

  const repostCar = async (query: { setLoading: any; id: string }) => {
    try {
      await execute(() => RepostCar(query), {
        successMessage: "Car reposted successfully",
        errorMessage: "Reposting Car Failed",

        onSuccess: (response) => {
          const { car: loadedCar, message, status, error } = response;

          if (status !== 200) throw new Error(message || error);

          dispatch(setCar(loadedCar));
          dispatch(setCarUpdateReload(true));
        },
      });
    } catch (error) {}
  };

  const deleteCar = async (query: DeleteCarProps) => {
    try {
      await execute(() => DeleteCar(query), {
        successMessage: "Car deleted successfully",
        errorMessage: "Deleting Car Failed",

        onSuccess: (response) => {
          const { car: loadedCar, message, status } = response;
        
          if (status !== 200) throw new Error(message);
        
          let tempCars = [...cars];
          tempCars = tempCars.filter((cr) => cr?._id !== loadedCar?._id);
        
          dispatch(setCars(tempCars));
          setTimeout(() => {
            navigate(-1);
          }, 200);
        },
      });
    } catch (error) {}
  };

  const getCarRequests = async (
    query: GetCarRequestsProps,
    { reload, local, more }: { reload?: boolean; local?: boolean; more?: boolean }
  ) => {
    if (
      carRequests &&
      carRequests.length > 0 &&
      !reload &&
      !local &&
      !more &&
      !carUpdateReload
    )
      return;
    try {
      await execute(() => GetCarRequests(query), {
        errorMessage: "Fetching Car Requests Failed",

        onSuccess: (response) => {
          const { requests, count, message, status } = response;

          if (status !== 200) throw new Error(message);
          
          let totalRequests = [...requests];
          
          if (local) return totalRequests;
          if (more) totalRequests = [...carRequests, ...requests];
          dispatch(setCarRequests(totalRequests));
        },
      });
    } catch (error) {}
  };

  return {
    getCars,
    getLiveCars,
    getCar,
    updateCar,
    repostCar,
    getUserCars,
    getCarByTag,
    deleteCar,
    getCarRequests,
  };
}
